import { formatAmount } from "@loot-os/api-contract";
import { Tooltip, TooltipContent, TooltipTrigger } from "@loot-os/ui/components/tooltip";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";

import { useCurrency } from "../lib/currency";
import { formatPreciseDateTime } from "../lib/format";
import { shopifyOrderNumber } from "../lib/shopifyStore";
import { useTRPC } from "../lib/trpc";
import { DetailField, DetailGroup } from "./DetailFields";
import { OrderStatusBadge } from "./OrderStatusBadge";
import { ShopifyOrderLink } from "./ShopifyLinks";

/**
 * Everything recorded about one Shopify box purchase, under the row it was opened from on the
 * customer page's Shopify tab or Shopify → Sales (`shopify.purchaseDetail`): the unit and its
 * buyer, the reveal and what it rolled, the $0 ship order a claim made, and the store credit the
 * unit sent to Shopify. Fetched on open, so a list stays one query.
 */
export function ShopifyPurchaseDetail({
  tenantId,
  purchaseId,
  shopHandle,
}: {
  tenantId: string;
  purchaseId: string;
  /** The store's admin handle, for links into Shopify; null prints the ids bare. */
  shopHandle: string | null;
}) {
  const trpc = useTRPC();
  const currency = useCurrency();
  const query = useQuery(trpc.shopify.purchaseDetail.queryOptions({ id: purchaseId }));

  if (query.isPending) {
    return <p className="py-3 text-sm text-muted-foreground">Loading the purchase…</p>;
  }
  if (query.error) {
    return <p className="py-3 text-sm text-destructive">{query.error.message}</p>;
  }
  const p = query.data;
  const { reveal, shipOrder } = p;
  const amount = (cents: number | null | undefined) =>
    cents === null || cents === undefined ? null : formatAmount(cents, currency);
  const when = (value: string | Date | null | undefined) =>
    value ? formatPreciseDateTime(value) : null;
  const shipOrderName = shipOrder?.shopifyOrderGid
    ? `#${shopifyOrderNumber(shipOrder.shopifyOrderGid)}`
    : null;

  return (
    // Its own container: the columns follow the width the panel is given, not the window's.
    <div className="@container">
      <div className="grid gap-x-12 gap-y-6 py-3 @3xl:grid-cols-2">
        <div className="flex min-w-0 flex-col gap-6">
          <DetailGroup title="Purchase">
            <DetailField label="Order" copy={p.orderName}>
              <ShopifyOrderLink
                shopHandle={shopHandle}
                orderGid={p.orderGid}
                className="text-primary hover:underline"
              >
                {p.orderName}
              </ShopifyOrderLink>
            </DetailField>
            <DetailField label="Unit">{p.unitIndex + 1}</DetailField>
            <DetailField label="Box">
              <Link
                to="/tenants/$tenantId/boxes/$boxId"
                params={{ tenantId, boxId: p.boxId }}
                className="text-primary hover:underline"
              >
                {p.boxName}
              </Link>
            </DetailField>
            <DetailField label="Paid">{amount(p.paidPriceCents)}</DetailField>
            <DetailField label="Status">{p.status}</DetailField>
            <DetailField label="Status changed">{when(p.statusChangedAt)}</DetailField>
            <DetailField label="Bought">{when(p.createdAt)}</DetailField>
            <DetailField label="Purchase id" mono copy={p.id}>
              {p.id}
            </DetailField>
            <DetailField label="Order id" mono copy={p.orderGid}>
              {p.orderGid}
            </DetailField>
            <DetailField label="Line item id" mono copy={p.lineItemId}>
              {p.lineItemId}
            </DetailField>
          </DetailGroup>

          <DetailGroup title="Customer">
            <DetailField label="Customer" copy={p.endUserId}>
              {p.endUserId && (
                <Link
                  to="/tenants/$tenantId/users/$userId"
                  params={{ tenantId, userId: p.endUserId }}
                  className="text-primary hover:underline"
                >
                  {p.endUserUsername ? `@${p.endUserUsername}` : p.endUserId}
                </Link>
              )}
            </DetailField>
            <DetailField label="External id" mono copy={p.endUserExternalId}>
              {p.endUserExternalId}
            </DetailField>
            <DetailField label="Checkout">{p.isGuest ? "guest" : "customer account"}</DetailField>
          </DetailGroup>
        </div>

        <div className="flex min-w-0 flex-col gap-6">
          <DetailGroup title="Reveal">
            {reveal ? (
              <>
                <DetailField label="Status">{reveal.soldAt ? "sold" : reveal.status}</DetailField>
                <DetailField label="Revealed">{when(reveal.createdAt)}</DetailField>
                <DetailField label="Claimed">{when(reveal.claimedAt)}</DetailField>
                <DetailField label="Sold">
                  {reveal.soldAt && `${amount(reveal.soldCents)} · ${when(reveal.soldAt)}`}
                </DetailField>
                {p.items.map((item) => (
                  <DetailField key={item.id} label="Item">
                    <ItemName name={item.name} imageUrl={item.imageUrl} />
                    <span className="ml-2 text-muted-foreground tabular-nums">
                      {amount(item.valueCents)}
                    </span>
                  </DetailField>
                ))}
                <DetailField label="Open group id" mono copy={reveal.id}>
                  {reveal.id}
                </DetailField>
              </>
            ) : (
              <p className="py-0.5 text-sm leading-5 text-muted-foreground">Not revealed yet.</p>
            )}
          </DetailGroup>

          <DetailGroup title="Ship order">
            {shipOrder ? (
              <>
                <DetailField label="Shopify order" copy={shipOrderName}>
                  {shipOrder.shopifyOrderGid && (
                    <ShopifyOrderLink
                      shopHandle={shopHandle}
                      orderGid={shipOrder.shopifyOrderGid}
                      className="text-primary hover:underline"
                    >
                      {shipOrderName}
                    </ShopifyOrderLink>
                  )}
                </DetailField>
                <DetailField label="Dispatch">
                  {shipOrder.dispatchStatus.replaceAll("_", " ")}
                  {shipOrder.attempts > 0 && (
                    <span className="ml-2 text-muted-foreground">
                      {shipOrder.attempts} {shipOrder.attempts === 1 ? "attempt" : "attempts"}
                    </span>
                  )}
                </DetailField>
                <DetailField label="Dispatched">{when(shipOrder.dispatchedAt)}</DetailField>
                <DetailField label="Last error">{shipOrder.lastError}</DetailField>
                {shipOrder.dispatchStatus === "out_of_stock" && (
                  <DetailField label="Noted in Shopify">
                    {when(shipOrder.shopifyAnnotatedAt) ?? "not yet"}
                  </DetailField>
                )}
                <DetailField label="Fulfillment">
                  <OrderStatusBadge status={shipOrder.orderStatus} />
                </DetailField>
                <DetailField label="Tracking" mono copy={shipOrder.trackingNumber}>
                  {shipOrder.trackingNumber}
                </DetailField>
                <DetailField label="Shipped">{when(shipOrder.shippedAt)}</DetailField>
                {shipOrder.cancelledAt && (
                  <DetailField label="Cancelled">
                    {when(shipOrder.cancelledAt)}
                    {shipOrder.cancellationReason && (
                      <span className="ml-2 text-muted-foreground">
                        {shipOrder.cancellationReason}
                      </span>
                    )}
                  </DetailField>
                )}
                <DetailField label="Loot OS order id" mono copy={shipOrder.orderId}>
                  {shipOrder.orderId}
                </DetailField>
              </>
            ) : (
              <p className="py-0.5 text-sm leading-5 text-muted-foreground">
                No ship order: the unit has not been claimed.
              </p>
            )}
          </DetailGroup>

          <DetailGroup title="Store credit">
            {p.transfers.length === 0 ? (
              <p className="py-0.5 text-sm leading-5 text-muted-foreground">
                No store credit moved for this unit.
              </p>
            ) : (
              p.transfers.map((t) => (
                <DetailField key={t.id} label={t.reason.replaceAll("_", " ")}>
                  <span className="tabular-nums">
                    {t.direction === "clawback" ? "−" : ""}
                    {amount(t.amountCents)}
                  </span>
                  <span className="ml-2 text-muted-foreground">
                    {t.status}
                    {t.appliedAt ? ` · ${when(t.appliedAt)}` : ` · ${when(t.createdAt)}`}
                  </span>
                  {t.lastError && <span className="block text-destructive">{t.lastError}</span>}
                </DetailField>
              ))
            )}
          </DetailGroup>
        </div>
      </div>
    </div>
  );
}

/** An item's name; hovering or focusing it shows the item's image when it has one. */
function ItemName({ name, imageUrl }: { name: string; imageUrl: string | null | undefined }) {
  if (!imageUrl) return name;
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <button
            type="button"
            className="cursor-default text-left underline decoration-dotted underline-offset-2"
          />
        }
      >
        {name}
      </TooltipTrigger>
      <TooltipContent side="right" className="max-w-none p-1">
        <img
          src={imageUrl}
          alt={name}
          className="size-40 rounded-sm bg-background object-contain"
        />
      </TooltipContent>
    </Tooltip>
  );
}
